import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { twMerge } from "tailwind-merge";
import { Badge } from "@themes/pollinator/ui/Badge";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { ActionCard } from "@themes/pollinator/ui/ActionCard";
import { TimezoneOption } from "@core/features/timezones";
import { AddToGoogleCalendarButton } from "@themes/pollinator/ui/AddToGoogleCalendarButton";
import { AddToICalButton } from "@themes/pollinator/ui/AddToICalButton";
import { ProgramPhaseActionItemContainer } from "@platform/features/programPhases";
import { isActionDisabledByDate, type Action } from "@core/features/actions";
import { Meeting } from "@platform/types/platform.types";

export type Props = {
  meeting?: Meeting;
  userTimezone?: TimezoneOption;
  onAddCalendarEvent: (
    meetingId: number,
    calendarType: "google" | "ical",
  ) => void;
  /** Whether this meeting is the current/active one */
  isActive?: boolean;
  /** Whether this item is expanded */
  isExpanded?: boolean;
  /** Toggle expand/collapse callback */
  onToggle?: () => void;
  /** Actions bound to this meeting (meeting_survey, meeting_reading, etc.) */
  meetingActions?: Action[];
  /** The meeting_attend action (data source for title/description) */
  meetingAction?: Action;
};

export function ProgramCalendarEventItem({
  meeting,
  userTimezone,
  onAddCalendarEvent,
  isActive = false,
  isExpanded = false,
  onToggle,
  meetingActions,
  meetingAction,
}: Props) {
  const navigate = useNavigate();

  if (!meeting || !userTimezone) return null;

  const eventUrl = `https://pollinator.coop/video/join/${meeting.id}`;
  const eventDescription = `Join your peer circle meeting on Pollinator:\n${eventUrl}\n\nVisit https://pollinator.coop/dashboard to review the meeting materials and prepare.`;

  const formattedTime = useMemo(() => {
    if (meeting.localStartTime) return meeting.localStartTime;
    if (!meeting.start_time) return undefined;
    return moment
      .tz(meeting.start_time, userTimezone.name)
      .format("ddd, MMM D [at] h:mm A z");
  }, [meeting.start_time, meeting.localStartTime, userTimezone.name]);

  const hasExpandableContent = meetingActions && meetingActions.length > 0;

  return (
    <ActionCard
      title={meetingAction?.name || meeting.title}
      subtitle={
        <div className="flex items-center gap-2">
          <Type variant="caption" className="text-brand-800">
            {formattedTime}
          </Type>
          {meeting.userRole && (
            <Badge>{meeting.userRole.name}</Badge>
          )}
        </div>
      }
      isExpanded={isExpanded}
      onToggle={hasExpandableContent ? onToggle : undefined}
    >
      <div className="flex flex-col gap-0">
        {meetingAction?.description && (
          <div className="px-5 pb-4 md:max-w-lg">
            <Type variant="body2">{meetingAction.description}</Type>
          </div>
        )}

        {meetingActions &&
          meetingActions.map((action) => (
            <ProgramPhaseActionItemContainer
              key={action.id}
              action={action}
              disabled={isActionDisabledByDate(action)}
              compact
            />
          ))}

        <div
          className={twMerge(
            "border-t border-brand-800 p-4 flex items-center justify-between rounded-b-lg",
            isActive && "bg-brand-100",
          )}
        >
          <div className="flex items-center gap-2">
            <AddToICalButton
              meeting={meeting}
              eventTitle={`Pollinator | ${meeting.title}`}
              eventDescription={eventDescription}
              eventUrl={eventUrl}
              onAddCalendarEvent={onAddCalendarEvent}
              variant="link-inline"
            />
            <AddToGoogleCalendarButton
              meeting={meeting}
              eventTitle={`Pollinator | ${meeting.title}`}
              eventDescription={eventDescription}
              eventUrl={eventUrl}
              onAddCalendarEvent={onAddCalendarEvent}
              variant="link-inline"
            />
          </div>
          {isActive && (
            <Button variant="primary" onClick={() => navigate("/video")}>
              Join Meeting
            </Button>
          )}
        </div>
      </div>
    </ActionCard>
  );
}

export default ProgramCalendarEventItem;
